<template>
  <section class="menu-image">
    <img class="menu-image-img" :src="menuImagePath" :alt="getNameByLocale(menuItem)" @error="handleError" />
    <div v-if="badge" class="menu-image-badge" :class="`menu-image-badge--${badgeSize}`">
      <img v-if="badge === 1" src="@/assets/images/badge-new.png" alt="신규 배지" />
      <img v-else-if="badge === 2" src="@/assets/images/badge-hot.png" alt="인기 배지" />
      <img v-else-if="badge === 3" src="@/assets/images/badge-recommended.png" alt="추천 배지" />
      <img v-else-if="badge === 4" src="@/assets/images/badge-spicy-mild.png" alt="맵기 1단계 배지" />
      <img v-else-if="badge === 5" src="@/assets/images/badge-spicy-medium.png" alt="맵기 2단계 배지" />
      <img v-else-if="badge === 6" src="@/assets/images/badge-spicy-hot.png" alt="맵기 3단계 배지" />
      <img v-else-if="badge === 7" src="@/assets/images/badge-signature.png" alt="시그니처 배지" />
      <img v-else-if="badge === 8" src="@/assets/images/badge-season.png" alt="시즌 메뉴 배지" />
      <img v-else-if="badge === 9" src="@/assets/images/badge-only-ice.png" alt="얼음만 배지" />
    </div>
    <div v-if="mainMenuBadgeActive" class="main-menu-badge">
      <img v-if="i18n.locale.value === LocaleLanguage.Ko" src="@/assets/images/badge-main-ko.png" alt="주메뉴 배지" />
      <img
        v-else-if="i18n.locale.value === LocaleLanguage.Ja"
        src="@/assets/images/badge-main-ja.png"
        alt="주메뉴 배지"
      />
      <img
        v-else-if="i18n.locale.value === LocaleLanguage.Zh"
        src="@/assets/images/badge-main-zh.png"
        alt="주메뉴 배지"
      />
      <img v-else src="@/assets/images/badge-main-en.png" alt="주메뉴 배지" />
    </div>
    <div v-if="oneLineDescription" class="menu-description">{{ oneLineDescription }}</div>
  </section>
</template>

<script setup lang="ts">
  import NoImage from '@/assets/images/no-image.svg';
  import { LocaleLanguage } from '@/enums/LocaleLanguage';
  import Menu from '@/interfaces/Menu';
  import ResponseItem from '@/interfaces/ResponseItem';
  import { useConfigStore } from '@/stores/config';
  import { getNameByLocale, getNoteByLocale } from '@/utils';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  interface Props {
    menuItem: Menu | ResponseItem;
    badge?: number;
    badgeSize?: 'medium' | 'large';
    mainMenuBadgeActive?: boolean;
    oneLineNote?: boolean;
    adImagePath?: string;
  }

  const i18n = useI18n();
  const configStore = useConfigStore();
  const props = defineProps<Props>();

  const menuImagePath = computed(() => {
    const basePath = `${import.meta.env.VITE_UPCLOUD_API_URL}/PosImage2`;
    let filePath = `/${configStore.config.StoreID}/${props.menuItem.ItemStoreCode}/BasicSetting`;

    if (props.adImagePath) {
      return `${import.meta.env.VITE_UPCLOUD_API_URL}/${props.adImagePath}`;
    } else if (props.menuItem.PictureFileName1) {
      filePath += `/MenuKiosk/${props.menuItem.PictureFileName1}`;
      return basePath + filePath;
    } else if (props.menuItem.PictureFileName2) {
      filePath += `/Menu/${props.menuItem.PictureFileName2}`;
      return basePath + filePath;
    } else {
      return NoImage;
    }
  });

  const oneLineDescription = computed(() => {
    // 사용 유무 체크
    if (!configStore.config.IsOneLineDescriptionUsed || !props.oneLineNote) return null;

    // 다국어 조회
    return getNoteByLocale(props.menuItem, true);
  });

  /** 이미지 에러 발생 시 본사(1000번 매장) 경로로 변경, 본사도 에러나는 경우 노이미지 설정 */
  const handleError = (e: Event) => {
    const previosSrc = (e.target as HTMLImageElement).src;
    if (previosSrc.indexOf('/1000/') >= 0) {
      (e.target as HTMLImageElement).src = NoImage;
    } else {
      (e.target as HTMLImageElement).src = previosSrc.replace(`/${props.menuItem.ItemStoreCode}/`, '/1000/');
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .menu-image {
    position: relative;

    .menu-image-img {
      width: 100%;
      aspect-ratio: 3 / 2;
      object-fit: scale-down;
    }
    .menu-image-badge {
      position: absolute;
      top: -0.2985vw;
      left: 0;
      display: flex;
      @media (max-width: vars.$md) {
        top: -3px;
      }
      img {
        height: 3.7313vw;
        @media (max-width: vars.$md) {
          height: 30px;
        }
      }
    }

    .menu-image-badge--large {
      img {
        @media (max-width: vars.$md) {
          height: 50px;
        }
      }
    }

    .main-menu-badge {
      position: absolute;
      top: 0;
      right: 0;
      img {
        height: 2.4627vw;
        @media (max-width: vars.$md) {
          height: 20px;
        }
      }
    }

    .menu-description {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 0.3731vw;
      background-color: rgb(0, 0, 0, 0.6);
      color: #fff;
      font-size: 1.1194vw;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }
</style>
